/* De offertelijst (/crm/quotes): ophalen, statustabs, zoeken, sorteren, en de filter
 * in de URL. Zelfde opzet als de projectlijst. Puur waar het kan, zodat het te
 * toetsen is; de client komt als argument binnen. */

import { isOpen, openSinds, weergave, type StatusVelden } from './status';

export interface LijstClient {
  from(tabel: string): any;
}

interface Ref {
  id: string;
  name: string;
}

export interface LijstOfferte extends StatusVelden {
  id: string;
  quote_number: string;
  title: string;
  status: string;
  total_amount: number | null;
  current_version: number | null;
  valid_until: string | null;
  quote_date: string | null;
  created_at: string;
  updated_at: string | null;
  sent_on: string | null;
  sent_to: string | null;
  decided_on: string | null;
  project: (Ref & { project_number: string | null }) | null;
  organization: Ref | null;
  charging_location: Ref | null;
  charging_opportunity: Ref | null;
}

const BASIS = 'id, quote_number, title, status, total_amount, current_version, valid_until, quote_date, created_at, updated_at';
const RELATIES = `organization:organizations(id, name), project:projects(id, name, project_number),
  charging_location:charging_locations(id, name), charging_opportunity:charging_opportunities(id, name)`;
const STATUSKOLOMMEN = 'sent_on, sent_to, decided_on';

const een = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);

export async function haalOffertelijst(client: LijstClient): Promise<LijstOfferte[]> {
  const vraag = (kolommen: string) =>
    client.from('quotes').select(`${kolommen}, ${RELATIES}`).is('deleted_at', null).order('updated_at', { ascending: false });
  let { data, error } = await vraag(`${BASIS}, ${STATUSKOLOMMEN}`);
  // Zolang migratie 097 er niet is, bestaan de statuskolommen nog niet: dan zonder.
  if (error?.code === '42703') ({ data, error } = await vraag(BASIS));
  if (error) throw error;
  return (data ?? []).map((q: any) => ({
    sent_on: null,
    sent_to: null,
    decided_on: null,
    ...q,
    total_amount: q.total_amount === null || q.total_amount === undefined ? null : Number(q.total_amount),
    project: een(q.project),
    organization: een(q.organization),
    charging_location: een(q.charging_location),
    charging_opportunity: een(q.charging_opportunity),
  }));
}

/* ── tabs ─────────────────────────────────────────────────────────── */

export const TABS = ['open', 'concept', 'verstuurd', 'verlopen', 'geaccepteerd', 'afgewezen', 'alle'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABEL: Record<Tab, string> = {
  open: 'Open',
  concept: 'Concept',
  verstuurd: 'Wacht op klant',
  verlopen: 'Verlopen',
  geaccepteerd: 'Geaccepteerd',
  afgewezen: 'Afgewezen',
  alle: 'Alle',
};

/** Open is concept plus verstuurd (ook als ze verlopen zijn); de andere tabs volgen de weergave. */
export function inTab(q: LijstOfferte, tab: Tab, vandaag: string): boolean {
  switch (tab) {
    case 'alle': return true;
    case 'open': return isOpen(q);
    case 'concept': return q.status === 'draft';
    case 'verstuurd': return q.status === 'sent';
    default: return weergave(q, vandaag) === tab;
  }
}

/* ── zoeken en sorteren ───────────────────────────────────────────── */

export type Sortering = 'bijgewerkt' | 'nummer' | 'titel' | 'bedrag' | 'open' | 'geldig';
export const SORTERINGEN: Sortering[] = ['bijgewerkt', 'nummer', 'titel', 'bedrag', 'open', 'geldig'];

export interface LijstFilter {
  zoek: string;
  tab: Tab;
  sortering: Sortering;
  oplopend: boolean;
}

export const STANDAARD_FILTER: LijstFilter = { zoek: '', tab: 'open', sortering: 'bijgewerkt', oplopend: false };

export interface Traject {
  soort: 'project' | 'kavel' | 'opportunity';
  label: string;
  href: string;
}

/** Waar de offerte bij hoort: project, Kavel of Opportunity (hoogstens één, migratie 032). */
export function traject(q: Pick<LijstOfferte, 'project' | 'charging_location' | 'charging_opportunity'>): Traject | null {
  if (q.project) return { soort: 'project', label: [q.project.project_number, q.project.name].filter(Boolean).join(' · '), href: `/crm/projects/${q.project.id}` };
  if (q.charging_location) return { soort: 'kavel', label: q.charging_location.name, href: `/crm/charging/locatie/${q.charging_location.id}` };
  if (q.charging_opportunity) return { soort: 'opportunity', label: q.charging_opportunity.name, href: `/crm/charging/opportunities/${q.charging_opportunity.id}` };
  return null;
}

export function passendBijZoek(q: LijstOfferte, zoek: string): boolean {
  const z = zoek.trim().toLowerCase();
  if (!z) return true;
  return [
    q.quote_number, q.title, q.project?.project_number, q.project?.name, q.organization?.name,
    q.charging_location?.name, q.charging_opportunity?.name, q.sent_to,
  ].some((v) => v?.toLowerCase().includes(z));
}

/** Offertenummer als getallen: P09 < P18 < P28-2 < P28-3; zonder cijfers achteraan. */
function nummerSleutel(q: LijstOfferte): number {
  const m = /(\d+)(?:-(\d+))?/.exec(q.quote_number ?? '');
  return m ? Number(m[1]) * 1000 + Number(m[2] ?? 0) : -1;
}

export function filterEnSorteer(lijst: LijstOfferte[], f: LijstFilter, vandaag: string): LijstOfferte[] {
  const uit = lijst.filter((q) => inTab(q, f.tab, vandaag) && passendBijZoek(q, f.zoek));
  const richting = f.oplopend ? 1 : -1;
  const sleutel = (q: LijstOfferte): number | string => {
    switch (f.sortering) {
      case 'nummer': return nummerSleutel(q);
      case 'titel': return q.title.toLowerCase();
      case 'bedrag': return q.total_amount ?? -1;
      // Oplopend = het langst open eerst; een besloten offerte staat niet open en gaat achteraan.
      case 'open': return openSinds(q) ?? (f.oplopend ? '9999' : '0000');
      case 'geldig': return q.valid_until ?? (f.oplopend ? '9999' : '0000');
      default: return q.updated_at ?? q.created_at;
    }
  };
  return uit.sort((a, b) => {
    const x = sleutel(a);
    const y = sleutel(b);
    if (x < y) return -1 * richting;
    if (x > y) return 1 * richting;
    return nummerSleutel(b) - nummerSleutel(a);
  });
}

export interface TabTelling {
  tab: Tab;
  aantal: number;
  waarde: number;
}

/** Telling per tab binnen de zoekterm, zodat de tabs kloppen met wat je ziet. */
export function tellingen(lijst: LijstOfferte[], zoek: string, vandaag: string): TabTelling[] {
  const basis = lijst.filter((q) => passendBijZoek(q, zoek));
  return TABS.map((tab) => {
    const inDeze = basis.filter((q) => inTab(q, tab, vandaag));
    return { tab, aantal: inDeze.length, waarde: inDeze.reduce((s, q) => s + (q.total_amount ?? 0), 0) };
  });
}

/* ── filter in de URL ─────────────────────────────────────────────── */

export function filterUitUrl(p: URLSearchParams): LijstFilter {
  const tab = p.get('tab');
  const sort = p.get('sort');
  return {
    zoek: p.get('q') ?? '',
    tab: TABS.includes(tab as Tab) ? (tab as Tab) : STANDAARD_FILTER.tab,
    sortering: SORTERINGEN.includes(sort as Sortering) ? (sort as Sortering) : STANDAARD_FILTER.sortering,
    oplopend: p.get('dir') === 'asc',
  };
}

export function filterNaarUrl(f: LijstFilter): Record<string, string> {
  const uit: Record<string, string> = {};
  if (f.zoek) uit.q = f.zoek;
  if (f.tab !== STANDAARD_FILTER.tab) uit.tab = f.tab;
  if (f.sortering !== STANDAARD_FILTER.sortering) uit.sort = f.sortering;
  if (f.oplopend !== STANDAARD_FILTER.oplopend) uit.dir = f.oplopend ? 'asc' : 'desc';
  return uit;
}

/* ── terug naar de lijst ──────────────────────────────────────────── */

/** sessionStorage: de zoekstring van de lijst, zodat "← Offertes" daar terugkomt. */
export const LIJST_SLEUTEL = 'offertelijst:zoek';

export function bewaarLijstAdres(zoek: string): void {
  try {
    sessionStorage.setItem(LIJST_SLEUTEL, zoek ? `?${zoek}` : '');
  } catch {
    /* privévenster of geen opslag: dan gaat de terugknop naar de lijst zonder filter */
  }
}

export function lijstAdres(): string {
  try {
    return `/crm/quotes${sessionStorage.getItem(LIJST_SLEUTEL) ?? ''}`;
  } catch {
    return '/crm/quotes';
  }
}
